import { useI18n } from "@/i18n";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  Wrench,
  Bot,
} from "lucide-react";

const ThreeLayers = () => {
  const { language } = useI18n();

  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              {language === 'en' ? 'From Chat to Creation to Automation' : '从对话，到创造，到自动化'}
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              {language === 'en'
                ? 'A complete AI platform that grows with your needs.'
                : '一个随你需求成长的完整 AI 平台。'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {/* Layer 1: AI Chat */}
            <div className="relative bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-all duration-300">
              <div className="mb-5 inline-flex p-3 rounded-xl bg-[#1E88C8]/10">
                <MessageSquare className="w-6 h-6 text-[#1E88C8]" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">
                {language === 'en' ? 'AI Chat' : 'AI 对话'}
              </h3>
              <p className="text-gray-500 mb-6 leading-relaxed">
                {language === 'en'
                  ? 'Chat with a powerful local LLM. Analyze documents, get writing help, with knowledge base support.'
                  : '与本地大语言模型对话。分析文档、获取写作帮助，支持知识库检索。'}
              </p>
              <ul className="space-y-3 text-sm text-gray-500">
                <li className="flex items-center gap-2">
                  <span className="text-[#1E88C8]">•</span>
                  {language === 'en' ? 'Real-time streaming responses' : '实时流式响应'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#1E88C8]">•</span>
                  {language === 'en' ? 'Upload & analyze documents' : '上传并分析文档'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#1E88C8]">•</span>
                  {language === 'en' ? 'Connect your own knowledge base' : '连接自有知识库'}
                </li>
              </ul>
            </div>

            {/* Layer 2: Bot Builder */}
            <div className="relative bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-all duration-300">
              <div className="mb-5 inline-flex p-3 rounded-xl bg-[#1E88C8]/10">
                <Wrench className="w-6 h-6 text-[#1E88C8]" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">
                {language === 'en' ? 'Bot Builder' : 'Bot 搭建器'}
              </h3>
              <p className="text-gray-500 mb-6 leading-relaxed">
                {language === 'en'
                  ? 'Create custom AI apps without code. Define personas, connect knowledge, publish instantly.'
                  : '无需代码创建 AI 应用。定义人格、连接知识源、一键发布。'}
              </p>
              <ul className="space-y-3 text-sm text-gray-500">
                <li className="flex items-center gap-2">
                  <span className="text-[#1E88C8]">•</span>
                  {language === 'en' ? 'Visual drag-and-drop builder' : '可视化拖拽搭建'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#1E88C8]">•</span>
                  {language === 'en' ? 'Per-bot knowledge base' : '独立知识库配置'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#1E88C8]">•</span>
                  {language === 'en' ? 'One-click publish to Bot Store' : '一键发布到 Bot Store'}
                </li>
              </ul>
            </div>

            {/* Layer 3: AI Agents */}
            <div className="relative bg-gray-50 rounded-2xl p-8 border border-[#F0C040]/40 hover:shadow-lg transition-all duration-300">
              <Badge className="absolute top-4 right-4 bg-[#F0C040]/15 text-[#B8860B] border border-[#F0C040]/40 hover:bg-[#F0C040]/25">
                {language === 'en' ? 'Coming Soon' : '即将推出'}
              </Badge>
              <div className="mb-5 inline-flex p-3 rounded-xl bg-[#1E88C8]/10">
                <Bot className="w-6 h-6 text-[#1E88C8]" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">
                {language === 'en' ? 'AI Agents' : 'AI Agent'}
              </h3>
              <p className="text-gray-500 mb-6 leading-relaxed">
                {language === 'en'
                  ? 'Automate sensitive workflows entirely on-device. Multi-step tasks where data privacy is non-negotiable.'
                  : '在设备上自动化敏感工作流。数据隐私不可妥协的多步骤任务。'}
              </p>
              <ul className="space-y-3 text-sm text-gray-500">
                <li className="flex items-center gap-2">
                  <span className="text-[#1E88C8]">•</span>
                  {language === 'en' ? 'Multi-step task chains' : '多步骤任务链'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#1E88C8]">•</span>
                  {language === 'en' ? 'On-device data processing' : '设备端数据处理'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#1E88C8]">•</span>
                  {language === 'en' ? 'Custom triggers & actions' : '自定义触发与动作'}
                </li>
              </ul>
            </div>
          </div>

          <p className="text-center mt-12 text-gray-400 text-sm">
            {language === 'en'
              ? 'Your most sensitive tasks deserve local AI. Estha keeps every byte on your device.'
              : '最敏感的任务，值得本地 AI。Estha 让每一个字节都留在你的设备上。'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ThreeLayers;
